import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { DoorOpen, Sparkles, Gamepad2, Check } from "lucide-react";
import Particles from "@/components/Particles";
import ChatPanel from "@/components/ChatPanel";
import StoryMoment from "@/components/StoryMoment";
import MiniGame from "@/components/MiniGame";
import { WORLD_EXTRAS, CHARACTER_EXTRAS } from "@/data";
import { useGame } from "@/context/GameContext";
import { sound } from "@/lib/sound";

const Avatar = ({ character, onClick, index, koreanSubs }) => {
  const ex = CHARACTER_EXTRAS[character.id];
  return (
    <motion.button
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.5 + index * 0.15, duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      whileHover={{ y: -6 }}
      onClick={onClick}
      data-testid={`character-${character.id}`}
      className="group flex w-44 flex-col items-center text-center"
    >
      <motion.div
        animate={{ y: [0, -8, 0], rotate: [-2, 2, -2] }}
        transition={{ duration: 5 + index, repeat: Infinity, ease: "easeInOut" }}
        className="relative h-28 w-28"
      >
        <div
          className="relative h-28 w-28 overflow-hidden rounded-full border-2"
          style={{ borderColor: character.accent }}
        >
          <div
            className="absolute inset-0 z-10 rounded-full opacity-0 transition-opacity duration-300 group-hover:opacity-100"
            style={{ boxShadow: `0 0 32px 4px ${character.accent}` }}
          />
          {character.avatar ? (
            <img
              src={character.avatar}
              alt={character.name}
              className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
            />
          ) : (
            <div
              className="flex h-full w-full items-center justify-center font-display text-3xl text-white"
              style={{ background: `linear-gradient(135deg, ${character.accent}, rgba(0,0,0,0.6))` }}
            >
              {character.name.charAt(0)}
            </div>
          )}
          {/* occasional blink */}
          <motion.div
            className="absolute inset-0 z-20 rounded-full bg-black"
            initial={{ opacity: 0 }}
            animate={{ opacity: [0, 0.85, 0] }}
            transition={{
              duration: 0.16,
              repeat: Infinity,
              repeatDelay: 4 + index * 0.7,
              ease: "easeInOut",
            }}
          />
        </div>
      </motion.div>
      <h4 className="mt-4 font-display text-lg tracking-tight text-white">
        {character.name}{" "}
        <span className="kr text-sm font-normal" style={{ color: character.accent }}>
          {character.koreanName}
        </span>
      </h4>
      {koreanSubs && (
        <p className="kr mt-0.5 text-[0.7rem]" style={{ color: character.accent }}>
          {ex.roleKr}
        </p>
      )}
      <p className="mt-1 text-xs leading-relaxed text-white/55">{character.bio}</p>
    </motion.button>
  );
};

const WorldView = ({ world, onBack }) => {
  const { state, completeMinigame } = useGame();
  const [activeCharacter, setActiveCharacter] = useState(null);
  const [storyOpen, setStoryOpen] = useState(false);
  const [gameOpen, setGameOpen] = useState(false);
  const koreanSubs = state.settings.koreanSubs;
  const extras = WORLD_EXTRAS[world.id];
  const gameDone = !!state.minigames[world.id];

  return (
    <motion.div
      key={`world-${world.id}`}
      className="relative min-h-screen w-full overflow-hidden"
      style={{ background: world.gradient }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1, transition: { duration: 0.8 } }}
      exit={{ opacity: 0, transition: { duration: 0.5 } }}
      data-testid={`world-screen-${world.id}`}
    >
      <Particles variant={world.particle} color={world.particleColor} />
      <Particles variant={extras.ambient} color={world.particleColor} />
      <div className="wv-grain" />
      <div
        className="pointer-events-none absolute inset-x-0 top-0 h-1.5"
        style={{ background: world.accent }}
      />

      {/* scene-setting */}
      <header className="relative z-10 mx-auto max-w-3xl px-6 pt-16 text-center sm:pt-20">
        <motion.span
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.6 }}
          className="kr mb-3 inline-block rounded-full border px-4 py-1 text-xs tracking-[0.2em]"
          style={{ borderColor: world.accentSoft, color: world.accent }}
        >
          {world.koreanName} · {world.name}
        </motion.span>
        <motion.p
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.45, duration: 0.7 }}
          className="text-lg leading-relaxed text-white/80 sm:text-xl"
          data-testid="scene-text"
        >
          {world.scene}
        </motion.p>
        {koreanSubs && (
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            className="kr mt-3 text-sm leading-relaxed text-white/55"
            data-testid="scene-text-kr"
          >
            {extras.sceneKr}
          </motion.p>
        )}
      </header>

      {/* characters */}
      <section className="relative z-10 mx-auto mt-16 flex max-w-4xl flex-wrap items-start justify-center gap-10 px-6 pb-40">
        {world.characters.map((c, i) => (
          <Avatar
            key={c.id}
            character={c}
            index={i}
            koreanSubs={koreanSubs}
            onClick={() => {
              sound.playClick();
              setActiveCharacter(c);
            }}
          />
        ))}
      </section>

      {/* back to lobby */}
      <button
        onClick={onBack}
        data-testid="back-to-lobby"
        className="fixed bottom-7 left-7 z-30 flex items-center gap-2 rounded-full border border-white/15 bg-black/50 px-5 py-3 text-sm text-white/80 backdrop-blur-md transition-colors hover:border-white/40 hover:text-white"
      >
        <DoorOpen className="h-4 w-4" /> Back to lobby
      </button>

      {/* centered action cluster */}
      <div className="fixed bottom-7 left-1/2 z-30 flex -translate-x-1/2 flex-wrap items-center justify-center gap-3">
        <button
          onClick={() => {
            sound.playClick();
            setStoryOpen(true);
          }}
          data-testid="trigger-story-moment"
          className="flex items-center gap-2 rounded-full border px-5 py-3 text-sm font-medium text-white backdrop-blur-md transition-transform hover:scale-105"
          style={{ borderColor: world.accent, background: `${world.accent}22`, boxShadow: `0 0 24px ${world.accentSoft}` }}
        >
          <Sparkles className="h-4 w-4" /> Trigger story moment
        </button>
        <button
          onClick={() => {
            sound.playClick();
            setGameOpen(true);
          }}
          data-testid="trigger-minigame"
          className="flex items-center gap-2 rounded-full border px-5 py-3 text-sm font-medium text-white backdrop-blur-md transition-transform hover:scale-105"
          style={{ borderColor: "rgba(255,255,255,0.2)", background: "rgba(255,255,255,0.06)" }}
        >
          <Gamepad2 className="h-4 w-4" /> {extras.minigame.name}
          {gameDone && <Check className="h-4 w-4 text-emerald-400" />}
        </button>
      </div>

      <AnimatePresence>
        {activeCharacter && (
          <ChatPanel
            character={activeCharacter}
            world={world}
            onClose={() => setActiveCharacter(null)}
          />
        )}
      </AnimatePresence>

      <AnimatePresence>
        {storyOpen && <StoryMoment world={world} onClose={() => setStoryOpen(false)} />}
      </AnimatePresence>

      <AnimatePresence>
        {gameOpen && (
          <MiniGame world={world} onClose={() => setGameOpen(false)} onComplete={completeMinigame} />
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default WorldView;
